import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '@config/AppConfigContext';
import { fetchJson, getAuthHeader, joinUrl } from '@hooks/requests/utils/_partner_api_client';
import { PARTNER_API_KEYS_PATH } from '@hooks/requests/utils/paths';

export interface RevokeApiKeyParams {
  /**
   * `PartnerApiKey.maskedKey` — not the real client_id, which this
   * frontend never has. The backend re-resolves the real client_id
   * server-side by matching this masked value's prefix against a fresh
   * read of the org's keys.
   */
  maskedKey: string;
}

export const useRevokeApiKeyMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ maskedKey }: RevokeApiKeyParams): Promise<void> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');
      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      await fetchJson<void>(
        joinUrl(allyApi, `${PARTNER_API_KEYS_PATH}/${encodeURIComponent(maskedKey)}`),
        authHeader,
        'Failed to delete the API key',
        { method: 'DELETE' }
      );
    },
    // A 404 here means someone else already deleted this key (or a 409 means
    // the account's key count moved under us) — the list still needs
    // refetching either way, not only on 2xx.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_API_KEYS_PATH] });
    },
  });
};

export default useRevokeApiKeyMutation;
